<template>
    <div>
        <div class="card card__subscription p-4 p-lg-6">
            <div v-if="subscriptionCancelled" class="alert alert-success mb-2">Subscription package successfully
                cancelled
            </div>
            <div v-if="subscriptionResumed" class="alert alert-success mb-2">Subscription package successfully
                resumed
            </div>
            <h2 class="t-orange mb-3">Update your subscription</h2>
            <p v-if="isSubscribedPastDueInvoices" class="p1 mb-3">
                You have one or more unpaid invoices. Please pay your past due invoices before the the end of the current billing period - <strong>{{ currentSubscriptionPeriodEnd }}</strong> - to continue using your <strong>{{currentPlan.name}}</strong> plan.
            </p>
            <p v-else-if="canCancelSubscription" class="p1 mb-3">You can upgrade/downgrade or cancel your <strong>{{currentPlan.name}}</strong> monthly
                subscription of <strong>£{{currentPlan.price}}</strong> at any time. If you do decide to cancel your payment then you will no longer have access to
                your contributions or CAT-UXO data after the end of the current billing period on <strong>{{ currentSubscriptionPeriodEnd }}</strong>.</p>
            <p v-else-if="canResumeSubscription" class="p1 mb-3">Your <strong>{{currentPlan.name}}</strong> monthly
                subscription ends on <strong>{{ resumeSubscriptionDate }}</strong>. If you wish to continue using the service features after this date, please resume your subscription.</p>
            <div class="d-sm-flex align-items-center">
                <pay-past-due-invoices v-if="hasPastDueInvoices"/>
                <button v-else-if="canUpdateSubscription" class="btn btn-form mr-4 mb-3 mb-sm-0" @click="showModal()">Edit Subscription</button>
                <template>
                    <cat-uxo-billing-resume-subscription v-if="canResumeSubscription" @success="subscriptionResumed = true"></cat-uxo-billing-resume-subscription>
                    <cat-uxo-billing-cancel-subscription v-else-if="canCancelSubscription" @success="subscriptionCancelled = true"></cat-uxo-billing-cancel-subscription>
                </template>
            </div>
        </div>
        <cat-uxo-modal v-if="modalVisible" @close="hideModal()">
            <h2 class="text-center t-orange" slot="header">Edit your subscription package</h2>
            <subscriptions slot="body" class="mb-5" :plans="plans">
                <a v-for="(plan,index) in plans" :key="plan.id" :slot="index === 0 ? 'buttonOne' : 'buttonTwo'"
                   class="btn w-100" :class="{'btn-ghost': selectedPlanId !== plan.id}"
                   @click="selectedPlan = plan.id">{{plan.id !== user.current_billing_plan ? 'Choose this plan' :
                    'Your current plan'}}</a>
            </subscriptions>
            <div class="d-flex justify-content-center flex-column align-items-center" slot="footer">
                <div v-if="subscriptionChanged" class="alert alert-success mb-2">Subscription package successfully
                    changed
                </div>
                <div v-if="errors && !Array.isArray(errors)" class="alert alert-error mb-2">{{errors}}</div>

                <button v-if="!loading" @click.prevent="submit()" class="btn btn-form">
                    Confirm changes
                </button>
                <pulse-loader v-if="loading" :loading="true" :color="'#E57722'"
                              :size="'20px'"></pulse-loader>
            </div>
        </cat-uxo-modal>
    </div>
</template>

<script>
    import Subscriptions from '../../Subscriptions'
    import MixinModal from '../../../mixins/modal'
    import CatUxoBillingCancelSubscription from './CancelSubScription';
    import CatUxoBillingResumeSubscription from './ResumeSubScription';
    import {axiosWeb} from "@/js/axios";
    import {mapActions, mapGetters, mapState} from "vuex";
    import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
    import PayPastDueInvoices from './PayPastDueInvoices';

    export default {
        name: 'CatUxoBillingUpdateSubscription',
        components: {
            Subscriptions,
            PulseLoader,
            CatUxoBillingCancelSubscription,
            CatUxoBillingResumeSubscription,
            PayPastDueInvoices
        },
        props: {
            plans: {
                type: Array,
                required: true
            }
        },
        mixins: [MixinModal],
        data() {
            return {
                currentPlan: {},
                loading: false,
                selectedPlan: null,
                errors: [],
                subscriptionChanged: false,
                subscriptionCancelled: false,
                subscriptionResumed: false,
            }
        },
        computed: {
            ...mapState('shared', [
                'stripeKey',
                'stripeOptions'
            ]),
            ...mapGetters('user', {
                user: 'activeUser',
                hasPastDueInvoices: 'hasPastDueInvoices',
                canResumeSubscription: 'canResumeSubscription',
                resumeSubscriptionDate: 'resumeSubscriptionDate',
                currentSubscriptionPeriodEnd: 'currentSubscriptionPeriodEnd',
                canCancelSubscription: 'canCancelSubscription',
                canUpdateSubscription: 'canUpdateSubscription',
                isSubscribed: 'isSubscribed',
                isSubscribedPastDueInvoices: 'isSubscribedPastDueInvoices',
            }),
            selectedPlanId() {
                if (!this.selectedPlan) return this.user.current_billing_plan;

                return this.selectedPlan;
            },
        },
        watch: {
            subscriptionCancelled(val) {
                const $this = this;
                if (val) {
                    setTimeout(() => $this.subscriptionCancelled = false, 3000);
                }
            },
            subscriptionResumed(val) {
                const $this = this;
                if (val) {
                    setTimeout(() => $this.subscriptionResumed = false, 3000);
                }
            },

            canUpdateSubscription: {
                immediate: true,
                deep: true,
                handler(val) {
                    console.log('Can update subscription', val);
                }
            }
        },
        methods: {
            ...mapActions('user', [
                'setUser'
            ]),
            submit() {
                let $this = this;
                $this.loading = true;

                return axiosWeb.post(`/api/v1/update-subscription`, {
                    customer: this.user.stripe_id,
                    plan: this.selectedPlanId,
                }).then(response => {
                    $this.setUser({user: response.data});
                    $this.loading = false;
                    $this.subscriptionChanged = true;

                    setTimeout(() => $this.subscriptionChanged = false, 3000);
                }).catch((error) => {
                    $this.loading = false;

                    if (error.response.status === 422) {
                        $this.errors = error.response.data.errors;
                    }

                    if (error.response.status === 500) {
                        $this.errors = 'Subscription package could not be changed.';
                        setTimeout(() => this.errors = false, 4000);
                    }
                })
            },
        },
        created() {
            for(let plan of this.plans) {
                if (plan.id === this.user.current_billing_plan) {
                    this.currentPlan.name = plan.name;
                   this.currentPlan.price = plan.price;
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    @include media-breakpoint-down(sm) {
        .btn,
        .btn-form,
        .btn-link {
            width: 100%;
            max-width: 100%;
        }

        .card__subscription {
            text-align: center;
        }
    }

    @include media-breakpoint-between(md, lg) {
        .btn {
            font-size: rem(16px);
        }
    }
</style>



// WEBPACK FOOTER //
// resources/js/components/pages/settings/UpdateSubScription.vue